import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { verifyMagicLink } from '../services/auth'
import { Truck, CircleNotch, CheckCircle, XCircle } from '@phosphor-icons/react'

type Status = 'loading' | 'success' | 'error'

export default function MagicLinkPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('loading')
  const [error, setError] = useState('')

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    async function verify() {
      if (!token || !email) {
        setStatus('error')
        setError('Link inválido. Token ou email não encontrado.')
        return
      }

      try {
        const data = await verifyMagicLink(email, token)
        setStatus('success')

        // Redirect based on user type after a short delay
        // Use window.location to force full page reload so AuthContext picks up new tokens
        setTimeout(() => {
          if (data.user?.user_master) {
            window.location.href = '/master/dashboard'
          } else if (data.user?.current_company?.type === 'freight-forwarder') {
            window.location.href = '/freight-forwarder/dashboard'
          } else {
            window.location.href = '/shipper/dashboard'
          }
        }, 1500)
      } catch (err: any) {
        setStatus('error')
        setError(err.response?.data?.error || 'Link inválido ou expirado. Solicite um novo link.')
      }
    }

    verify()
  }, [token, email, navigate])

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center" style={{ background: 'rgb(37, 48, 55)' }}>
      {/* Logo */}
      <div className="flex items-center gap-3 mb-8">
        <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
          <Truck size={32} weight="bold" className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white">Eximtech</h1>
      </div>

      {/* Card */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/40 max-w-md w-full mx-4">
        {status === 'loading' && (
          <div className="text-center">
            <CircleNotch size={48} weight="bold" className="text-green-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Verificando seu acesso...</h2>
            <p className="text-slate-400">Aguarde um momento</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircle size={48} weight="bold" className="text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Acesso verificado!</h2>
            <p className="text-slate-400">Redirecionando para o dashboard...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <XCircle size={48} weight="bold" className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Erro na verificação</h2>
            <p className="text-red-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full text-white font-medium py-2.5 px-4 rounded-lg transition-all hover:shadow-lg"
              style={{ backgroundColor: 'rgb(5, 150, 105)' }}
            >
              Voltar para o login
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
